import React, { ReactElement, useEffect, useRef, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  Box,
  Button,
  Card,
  Divider,
  IconButton,
  LinearProgress,
  Typography,
} from '@mui/material'
import MuxPlayer from '@mux/mux-player-react'

import axios, { pluckData } from 'src/utilities/http-client'
import {
  QuestionSessionData,
  QuizSessionAnswer,
  QuizSessionResult,
  QuizV2,
} from 'src/v3/core/types'
import {
  CloseIcon,
  ConfirmIcon,
  LeftArrowIcon,
  RightArrowIcon,
} from '../../core/icons'
import EmptyCircleIcon from '@mui/icons-material/PanoramaFishEye'
import FullCircleIcon from '@mui/icons-material/RadioButtonChecked'
import { selectUserID } from '../../settings/settings.slice'

type AnswerResponse = {
  isCorrect: boolean
  correctAnswer: string
  explanation: string
}

type ResultWithAnswers = QuizSessionResult & { answers: QuizSessionAnswer[] }

export let QuizTaker = ({
  name,
  quiz,
  questionData,
  apiPath,
  proceedButton,
  existingResult,
}: {
  name: string
  quiz: QuizV2
  questionData: QuestionSessionData[]
  apiPath: string
  proceedButton: ReactElement
  existingResult?: ResultWithAnswers
}): ReactElement => {
  let dispatch = useAppDispatch()
  let playerRef = useRef(null)

  let userID = useAppSelector(selectUserID)

  let [sessionID, setSessionID] = useState<string>('')
  let [questionIndex, setQuestionIndex] = useState<number>(0)
  let [selectedAnswer, setSelectedAnswer] = useState<string>('')
  let [answerResponse, setAnswerResponse] = useState<AnswerResponse>()
  let [quizResult, setQuizResult] = useState<ResultWithAnswers>()
  let [loading, setLoading] = useState<boolean>(false)
  let [nextLoading, setNextLoading] = useState<boolean>(false)

  let currentResult = existingResult || quizResult
  let currentQuestion = questionData?.[questionIndex]?.quizQuestion
  let currentAnswers = questionData?.[questionIndex]?.questionAnswers

  useEffect(() => {
    if (quiz?.id && userID && !sessionID) {
      axios
        .post<string>(`${apiPath}/quiz/session`, {
          quizID: quiz.id,
          userID,
        })
        .then(pluckData)
        .then((id) => setSessionID(id))
    }
  }, [quiz, userID, sessionID])

  useEffect(() => {
    if (questionData) {
      let i = questionData.findIndex((qd) => qd.isCurrent)
      if (i > -1) setQuestionIndex(i)
    }
  }, [questionData])

  let videoName = `${name} Question Image`

  return (
    <>
      <Typography variant="h5">{name} Quiz</Typography>
      {!currentResult ? (
        <>
          <LinearProgress
            variant="determinate"
            color="inherit"
            value={(questionIndex / questionData?.length) * 100}
            sx={{ mb: 0.5, mt: 2, height: 8, borderRadius: 1 }}
          />
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}
          >
            <Typography variant="subtitle2" color="textSecondary">
              Question {questionIndex + 1} / {questionData?.length}
            </Typography>
            <Typography variant="subtitle2" color="textSecondary">
              {Math.round((questionIndex / questionData?.length) * 100)}%
              Complete
            </Typography>
          </Box>
          <Card
            variant="outlined"
            sx={{ p: 2, mt: 2, bgcolor: 'background.paperDark' }}
          >
            <Typography variant="h6">Question {questionIndex + 1}</Typography>
            {(currentQuestion?.imageName || currentQuestion?.videoName) && (
              <Box sx={{ mt: 1 }}>
                {currentQuestion?.imageName && (
                  <img
                    style={{ maxHeight: 200, border: '1px solid #6d6d6d' }}
                    src={`${import.meta.env.VITE_CLOUDFRONT_URL}/images/${currentQuestion.imageName?.replace('originals', 'resized')}`}
                  />
                )}
                {currentQuestion?.videoName && (
                  <MuxPlayer
                    title={videoName}
                    ref={playerRef}
                    src={`${import.meta.env.VITE_CLOUDFRONT_URL}/public/${currentQuestion.videoName.replace('mp4', 'm3u8')}`}
                    accentColor="#f9ea4f"
                    primaryColor="#6694E9"
                    backwardSeekOffset={20}
                    forwardSeekOffset={20}
                    metadata={{
                      video_id: currentQuestion.id,
                      video_title: videoName,
                      viewer_user_id: userID,
                    }}
                    defaultHiddenCaptions={true}
                    streamType="on-demand"
                  />
                )}
              </Box>
            )}
            <Typography sx={{ my: 2 }}>{currentQuestion?.text}</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {currentAnswers?.map((a) => (
                <Card
                  key={a.id}
                  sx={{
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    cursor: !answerResponse ? 'pointer' : undefined,
                    '&:hover': !answerResponse
                      ? { bgcolor: '#191919' }
                      : undefined,
                    color:
                      answerResponse && selectedAnswer === a.id
                        ? answerResponse.correctAnswer === a.id
                          ? 'success.main'
                          : 'error.main'
                        : undefined,
                  }}
                  onClick={
                    !answerResponse ? () => setSelectedAnswer(a.id) : undefined
                  }
                >
                  {!answerResponse ? (
                    <IconButton
                      size="small"
                      onClick={() => setSelectedAnswer(a.id)}
                    >
                      {selectedAnswer === a.id ? (
                        <FullCircleIcon color="primary" />
                      ) : (
                        <EmptyCircleIcon />
                      )}
                    </IconButton>
                  ) : (
                    <Box
                      sx={{
                        height: 34,
                        width: 34,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      {selectedAnswer === a.id ||
                      a.id === answerResponse.correctAnswer ? (
                        answerResponse.correctAnswer === a.id ? (
                          <ConfirmIcon color="success" />
                        ) : (
                          <CloseIcon color="error" />
                        )
                      ) : (
                        <></>
                      )}
                    </Box>
                  )}
                  <Typography>{a.text}</Typography>
                </Card>
              ))}
            </Box>
            {loading && <LinearProgress sx={{ mt: 2 }} />}
            {answerResponse && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {answerResponse.isCorrect ? 'Correct!' : 'Incorrect:'}
                </Typography>
                <Typography variant="subtitle1">
                  {answerResponse.explanation}
                </Typography>
              </Box>
            )}
          </Card>
          {nextLoading && <LinearProgress sx={{ mt: 2 }} />}
          <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
            <Button
              sx={{ color: 'text.primary', textTransform: 'none' }}
              startIcon={<LeftArrowIcon />}
              onClick={() => setQuestionIndex(questionIndex - 1)}
              disabled={questionIndex === 0}
            >
              Previous
            </Button>
            <Box sx={{ flexGrow: 1 }}></Box>
            {/* <Button
                sx={{ color: 'text.primary', textTransform: 'none' }}
                onClick={() => setQuestionIndex(questionIndex + 1)}
              >
                Skip
              </Button> */}
            {!answerResponse ? (
              <Button
                sx={{ textTransform: 'none' }}
                variant="contained"
                startIcon={<ConfirmIcon />}
                onClick={() => {
                  setLoading(true)
                  axios
                    .post<AnswerResponse>(`${apiPath}/quiz/session/answer`, {
                      sessionID,
                      questionID: currentQuestion.id,
                      answerID: selectedAnswer,
                      userID,
                    })
                    .then(pluckData)
                    .then((response) => setAnswerResponse(response))
                    .finally(() => setLoading(false))
                }}
                disabled={!selectedAnswer}
              >
                Submit
              </Button>
            ) : (
              <Button
                sx={{ textTransform: 'none' }}
                variant="contained"
                endIcon={<RightArrowIcon />}
                onClick={() => {
                  setNextLoading(true)
                  setSelectedAnswer('')
                  setAnswerResponse(undefined)
                  if (questionIndex + 1 === questionData.length) {
                    axios
                      .post<ResultWithAnswers>(
                        `${apiPath}/quiz/session/result`,
                        {
                          sessionID,
                        },
                      )
                      .then(pluckData)
                      .then((result) => setQuizResult(result))
                      .finally(() => setNextLoading(false))
                  } else {
                    axios
                      .post(`${apiPath}/quiz/session/question`, {
                        sessionID,
                        questionID:
                          questionData[questionIndex + 1].quizQuestion.id,
                      })
                      .then(() => setQuestionIndex(questionIndex + 1))
                      .finally(() => setNextLoading(false))
                  }
                }}
              >
                Next
              </Button>
            )}
          </Box>
        </>
      ) : (
        <>
          <Typography sx={{ mt: 2, mb: 1 }}>
            {currentResult.passed
              ? 'Congratulations! You have passed this quiz!'
              : 'You did not pass this quiz. To get credit, you will need to retake the quiz.'}
          </Typography>
          {currentResult.passed && <Box>{proceedButton}</Box>}
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>
            Question Review
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {currentResult?.answers.map((a) => (
              <Card key={a.id}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5 }}
                >
                  <Box sx={{ pb: '3px' }}>
                    {a.isCorrect ? (
                      <ConfirmIcon color="success" />
                    ) : (
                      <CloseIcon color="error" />
                    )}
                  </Box>
                  <Typography>
                    {
                      questionData?.find(
                        (qd) => qd.quizQuestion.id === a.questionID,
                      )?.quizQuestion.text
                    }
                  </Typography>
                </Box>
              </Card>
            ))}
          </Box>
        </>
      )}
    </>
  )
}
